import { useState } from 'react';
import { useTranslation } from '../i18n';
import CardNB from './ui-nb/CardNB';
import ButtonNB from './ui-nb/ButtonNB';
import { supabase } from '../lib/supabase';

interface ContactFormProps {
  variant?: 'support' | 'contact';
}

type FormStatus = 'idle' | 'loading' | 'success' | 'error';

const SUPPORT_EMAIL = 'support@tellowup.com';
const CONTACT_EMAIL = 'hello@tellowup.com';

export default function ContactForm({ variant = 'support' }: ContactFormProps) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState<FormStatus>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const recipient = variant === 'support' ? SUPPORT_EMAIL : CONTACT_EMAIL;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim() || !email.trim() || !message.trim()) {
      return;
    }

    setStatus('loading');
    setErrorMessage('');

    try {
      const { data, error } = await supabase.functions.invoke('send-contact-email', {
        body: { name, email, message },
      });

      if (error || !data?.success) {
        throw error || new Error('Submission failed');
      }

      setStatus('success');
    } catch {
      // Fallback to mailto - always success path per spec
      const subject = encodeURIComponent(`${variant === 'support' ? 'Support' : 'Contact'} Request from ${name}`);
      const body = encodeURIComponent(
        `Name: ${name}\nEmail: ${email}\n\nMessage:\n${message}`
      );
      window.open(`mailto:${recipient}?subject=${subject}&body=${body}`, '_blank');
      setStatus('success');
    }
  };

  if (status === 'success') {
    return (
      <CardNB>
        <p style={{ color: '#000000', fontWeight: 500 }}>
          {t('contactForm.success', 'Message sent! We\'ll get back to you soon.')}
        </p>
      </CardNB>
    );
  }

  return (
    <CardNB>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <div>
          <label htmlFor="contact-name" style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.25rem', color: '#000' }}>
            {t('contactForm.nameLabel', 'Your name')}
          </label>
          <input
            id="contact-name"
            name="name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            style={{ width: '100%', padding: '0.5rem 0.75rem', border: '2px solid #000', fontSize: '0.875rem' }}
          />
        </div>

        <div>
          <label htmlFor="contact-email" style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.25rem', color: '#000' }}>
            {t('contactForm.emailLabel', 'Email address')}
          </label>
          <input
            id="contact-email"
            name="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            style={{ width: '100%', padding: '0.5rem 0.75rem', border: '2px solid #000', fontSize: '0.875rem' }}
          />
        </div>

        <div>
          <label htmlFor="contact-message" style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.25rem', color: '#000' }}>
            {t('contactForm.messageLabel', 'How can we help?')}
          </label>
          <textarea
            id="contact-message"
            name="message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            required
            rows={4}
            style={{ width: '100%', padding: '0.5rem 0.75rem', border: '2px solid #000', fontSize: '0.875rem', resize: 'vertical' }}
          />
        </div>

        {status === 'error' && (
          <p style={{ color: '#ef4444', fontSize: '0.875rem' }}>
            {errorMessage || t('contactForm.error', 'Failed to send. Please try again.')}
          </p>
        )}

        <ButtonNB
          type="submit"
          variant="primary"
          disabled={status === 'loading'}
          loading={status === 'loading'}
        >
          {status === 'loading'
            ? t('contactForm.submitting', 'Sending...')
            : t('contactForm.submit', 'Send message')}
        </ButtonNB>
      </form>
    </CardNB>
  );
}
